/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ButtonBase, Stack } from '@mui/material';
import { Button } from '../../../../components/Button';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { ModalActions } from '../../../../components/Modal';
import Typography from '../../../../components/Typography';
import TestIds from '../../../../testIds';
import { colors } from '@farmlands/mui-theme';
import Input from '../../../../components/Input';
import { AccountAddress } from '../../../azure-b2c/storage';
import { AddressLine, FullAddress } from '../../../../services/als';
import { useCampaignContext } from './CampaignContext';
import { getFullAddressForCampaign } from './utils';
import TextField from '../../../../components/TextField';
import { PadLineItem } from '../../../../components/Pad/Pad';
import { splitAddress } from '../../../../services/als/splitAddress';
import useAccountAddress from '../../../../util/useAccountAddress';
import React, { useEffect } from 'react';

const DeliveryAddress = () => {
  const { state, setState } = useCampaignContext();

  function handleOnAddressSelect(address: AccountAddress | FullAddress) {
    setState({ addressSelected: address });
  }

  if (state.addressSelected) {
    return <AddressSelectedView address={state.addressSelected} />;
  }

  return <SearchAddresView onAddressSelected={handleOnAddressSelect} />;
};

interface AddressSelectedViewProps {
  address: AccountAddress | AddressLine;
}

function AddressSelectedView({ address }: Readonly<AddressSelectedViewProps>) {
  const { state, setState } = useCampaignContext();
  const formulateAddressLabel = React.useMemo(() => {
    return getFullAddressForCampaign(address);
  }, [address]);

  function handleSubmitNextStep() {
    if (name.length < 1) {
      setError('Please enter an address name');
      return;
    }

    setError(undefined);
    setState({
      addressSelected: {
        ...state.addressSelected,
        name: name,
      } as AccountAddress,
      tabIndex: state.tabIndex + 1,
    });
  }

  const [error, setError] = React.useState<string>();

  const [name, setName] = React.useState<string>('');

  useEffect(() => {
    if (
      state.addressSelected &&
      'name' in state.addressSelected &&
      state.addressSelected.name.length > 1
    ) {
      setName(state.addressSelected.name);
    }
  }, [state.addressSelected]);

  const needsAddressName = React.useMemo(() => {
    if (
      state.addressSelected &&
      'recordId' in state.addressSelected &&
      state.addressSelected.recordId &&
      state.addressSelected.name.length > 1
    ) {
      return false;
    } else if (state.addressSelected) {
      return true;
    }

    return false;
  }, [state.addressSelected]);

  const getAddressNameInput = () => {
    if (needsAddressName) {
      const props = error
        ? {
            error: Boolean(error),
            errorMessage: error,
          }
        : {
            helperText: 'Name this address for future use.',
            helperTextColor: colors.grey700,
          };
      return (
        <Input
          label='Address name'
          testId={TestIds.feat.campaign.fulfilmentTab.searchInput}
          type='text'
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          required
          {...props}
        />
      );
    }
  };

  return (
    <Stack flexDirection='column' gap={2}>
      <Stack
        direction='row'
        p={1}
        bgcolor={colors.primary100}
        borderRadius={2}
        alignItems='center'
        justifyContent='space-between'
      >
        <Stack direction='column' borderLeft='3px solid #00553D' px={1}>
          <Typography variant='body2' fontWeight='bold' color='primary500'>
            Deliver to:
          </Typography>
          <Typography variant='body2' color='primary500'>
            {formulateAddressLabel.name}
          </Typography>
          <Typography variant='body2' color='grey800'>
            {formulateAddressLabel.fullAddress}
          </Typography>
        </Stack>
        <ButtonBase
          data-testid={TestIds.feat.campaign.fulfilmentTab.editDelivery}
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
          disableRipple
          onClick={() => setState({ addressSelected: undefined })}
        >
          <Typography variant='body2' color='primary500'>
            Edit
          </Typography>
          <MaterialSymbol sx={{ fontSize: '16px', color: colors.primary500 }}>edit</MaterialSymbol>
        </ButtonBase>
      </Stack>

      {getAddressNameInput()}

      <ModalActions showDivider>
        <Button
          key='next-step-btn'
          variant='contained'
          onClick={() => {
            handleSubmitNextStep();
          }}
          data-testid={TestIds.feat.campaign.nextButton}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next Step
        </Button>
      </ModalActions>
    </Stack>
  );
}

interface SearchAddressViewProps {
  onAddressSelected: (addressInfo: AccountAddress | FullAddress) => void;
}

const SearchAddresView: React.FC<SearchAddressViewProps> = ({ onAddressSelected }) => {
  const [addressText, setAddressText] = React.useState('');
  const { matchingKnownAddresses, searchAddressQuery, getFullAddressMutation } = useAccountAddress({
    searchText: addressText,
  });

  const searchAddressResult = React.useMemo(() => {
    if (searchAddressQuery.isSuccess) {
      return searchAddressQuery.data;
    }
    return [];
  }, [searchAddressQuery.isSuccess, searchAddressQuery.data]);

  const handleNewAddressSelect = async (address: AddressLine) => {
    try {
      if (address.UniqueId) {
        const fullAddress = await getFullAddressMutation.mutateAsync(address.UniqueId);
        onAddressSelected(fullAddress);
      }
    } catch {
      console.log('something wrong');
    }
  };

  return (
    <Stack direction='column' gap={2}>
      <TextField
        label={'Search address:'}
        value={addressText}
        placeholder='Add new delivery address or select existing delivery address'
        testId={TestIds.feat.campaign.fulfilmentTab.searchInput}
        variant={'outlined'}
        onChange={(e) => setAddressText(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        inputProps={{
          maxLength: 150,
        }}
        InputProps={{
          endAdornment: <MaterialSymbol sx={{ color: colors.primary500 }}>search</MaterialSymbol>,
        }}
      />
      <Stack direction='column'>
        {matchingKnownAddresses.map((matchAddress) => {
          return (
            <PadLineItem
              option={{
                title: (
                  <Typography variant='body2' component='span' color={'primary700'}>
                    {matchAddress.name && (
                      <Typography
                        variant='body2'
                        component={'span'}
                        color={'primary700'}
                        fontWeight={700}
                      >
                        {matchAddress.name}
                        {': '}
                      </Typography>
                    )}
                    <br />
                    {matchAddress.street}
                  </Typography>
                ),
                subtitle: `${matchAddress.city}, ${matchAddress.zipCode}`,
              }}
              data-testid={TestIds.components.address.knownAddressItem}
              data-id={TestIds.components.address.knownAddressDataId(matchAddress.name)}
              key={`${matchAddress.fullAddress}-${matchAddress.name}-${matchAddress.dairySupplyNum}`}
              onClick={() => onAddressSelected(matchAddress)}
            />
          );
        })}
        {searchAddressResult.map((address) => {
          const { first, second } = splitAddress(address.FullAddress);
          return (
            <PadLineItem
              option={{
                title: first,
                subtitle: second,
              }}
              data-testid={TestIds.components.address.searchAddressItem}
              data-id={TestIds.components.address.searchAddressDataId(address.UniqueId.toString())}
              key={address.FullAddress}
              onClick={() => {
                handleNewAddressSelect(address);
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default DeliveryAddress;
